import { createSlice } from "@reduxjs/toolkit";
import {
  getWhatsappConversationsThunk,
  getWhatsappConversationByIdThunk,
  updateWhatsappConversationStatusThunk,
  getWhatsappStatsThunk,
  getWhatsappMessageStatsThunk,
} from "../actions/whatsapp.action";
import {
  IWhatsappConversation,
  IWhatsappPagination,
  IWhatsappStats,
} from "../../models/whatsapp.model";

export interface WhatsappState {
  conversations: {
    messages: IWhatsappConversation[];
    pagination: IWhatsappPagination;
    count: number;
  };
  currentConversation: IWhatsappConversation | null;
  stats: IWhatsappStats | null;
  messageStats: any | null;
  statsLoading: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: WhatsappState = {
  conversations: {
    messages: [],
    pagination: {
      totalItems: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: 10,
    },
    count: 0,
  },
  currentConversation: null,
  stats: null,
  messageStats: null,
  statsLoading: false,
  loading: false,
  error: null,
};

const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentConversation: (state) => {
      state.currentConversation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWhatsappConversationsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getWhatsappConversationsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.data;
      })
      .addCase(getWhatsappConversationsThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
    builder
      .addCase(getWhatsappConversationByIdThunk.pending, (state) => {
        // Only show loading spinner on initial load, not during polling
        if (!state.currentConversation) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(getWhatsappConversationByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        const next = action.payload.data;
        if (
          state.currentConversation?._id === next?._id &&
          state.currentConversation?.history &&
          !next?.history
        ) {
          next.history = state.currentConversation.history;
        }
        state.currentConversation = next;
      })
      .addCase(getWhatsappConversationByIdThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
    builder
      .addCase(updateWhatsappConversationStatusThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(updateWhatsappConversationStatusThunk.fulfilled, (state, action) => {
        const { id, status } = action.meta.arg;
        const applyStatus = (conversation: IWhatsappConversation) => {
          const token = String(status || "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_");
          conversation.status = (token || status) as IWhatsappConversation["status"];
          if (token === "need_review" || token === "need_assign" || token === "escalated") {
            conversation.awaiting = "us";
            conversation.state = "need_assign";
            conversation.handling_mode = "human";
            conversation.assigned_to = {
              role: "user",
              source: "system_escalated",
            };
            conversation.resolution = null;
          } else if (token === "open") {
            conversation.awaiting = "customer";
            conversation.state = "open";
            conversation.handling_mode = "ai_autonomous";
            conversation.resolution = null;
          } else if (token === "assigned") {
            conversation.state = "open";
            conversation.handling_mode = "human";
            conversation.awaiting = "us";
          } else if (token === "failed") {
            conversation.state = "failed";
            conversation.resolution = "failed";
          } else if (
            token === "completed" ||
            token === "resolved" ||
            token === "closed" ||
            token === "handled_by_ai"
          ) {
            conversation.state = "closed";
          }
        };
        if (state.currentConversation?._id === id) {
          applyStatus(state.currentConversation);
        }
        const conversationIndex = state.conversations.messages.findIndex(
          (conv) => conv._id === id
        );
        if (conversationIndex !== -1) {
          applyStatus(state.conversations.messages[conversationIndex]);
        }
      })
      .addCase(updateWhatsappConversationStatusThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    builder
      .addCase(getWhatsappStatsThunk.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(getWhatsappStatsThunk.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload.data;
      })
      .addCase(getWhatsappStatsThunk.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(getWhatsappMessageStatsThunk.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(getWhatsappMessageStatsThunk.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.messageStats = action.payload.data;
      })
      .addCase(getWhatsappMessageStatsThunk.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentConversation } = whatsappSlice.actions;
export default whatsappSlice.reducer;
